import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { useLocation } from "react-router-dom";
import UploadDesignModal from "./UploadDesignModal";
import "../../assets/css/OrderDetailsPage.css";

const normalizeText = (text) => {
  if (!text) return "";

  return text
    .toString()
    .toLocaleLowerCase("en-US")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/Ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/Ç/g, "c")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const getColorValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    if (name.includes("color") || name.includes("renk")) {
      return opt.value?.trim() || "-";
    }
  }
  return "-";
};

const getOptionValue = (options, keywords) => {
  if (!Array.isArray(options)) return "";
  const normalized = (text) =>
    text
      .toLocaleLowerCase("en-US")
      .replace(/ı/g, "i")
      .replace(/İ/g, "i")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  const match = options.find((opt) =>
    keywords.some((keyword) => normalized(opt.name) === normalized(keyword))
  );
  return match?.value || "-";
};

const getSizeValue = (options) => {
  if (!Array.isArray(options)) return "";
  for (let opt of options) {
    const name = normalizeText(opt.name);
    const value = opt.value?.trim() || "";
    if (
      name.includes("size") ||
      name.includes("sıze") ||
      name.includes("beden") ||
      value.includes(" - ") // örn: "Sweatshirt - L"
    ) {
      return value;
    }
  }
  return "-";
};

// 🆕 Product Type Tespit - KAPSAMLI VERSİYON
const detectProductType = (item) => {
  if (!item?.name) {
    return "Tshirt";
  }

  const name = (item.name || "").toLowerCase();
  const options = item.options || [];

  const sizeOption = options.find(
    (opt) =>
      normalizeText(opt.name).includes("size") ||
      normalizeText(opt.name).includes("beden")
  );

  const colorOption = options.find(
    (opt) =>
      normalizeText(opt.name).includes("color") ||
      normalizeText(opt.name).includes("colour") ||
      normalizeText(opt.name).includes("renk")
  );

  const sizeValue = (sizeOption?.value || "").toLowerCase();
  const colorValue = (colorOption?.value || "").toLowerCase();

  const combinedText = `${name} ${sizeValue} ${colorValue}`;

  const productPatterns = [
    { pattern: /racerback|tank.*top/i, type: "Racerback" },
    { pattern: /crop.*tank|crop.*top/i, type: "Crop" },
    { pattern: /v.*neck|v-neck/i, type: "V-neck" },
    { pattern: /hoodie|hoody/i, type: "Hoodie" },
    { pattern: /sweatshirt|sweat.*shirt/i, type: "Sweatshirt" },
    { pattern: /polo|polo.*shirt/i, type: "Polo" },
    { pattern: /long.*sleeve|long.*sleeved/i, type: "Long Sleeve" },
    { pattern: /short.*sleeve|short.*sleeved/i, type: "Tshirt" },
    { pattern: /t.*shirt|tshirt|tee|shirt/i, type: "Tshirt" },
  ];

  // Önce size alanında ara
  if (sizeValue) {
    for (const { pattern, type } of productPatterns) {
      if (pattern.test(sizeValue)) {
        return type;
      }
    }

    // Basit eşleşmeler
    if (sizeValue.includes("hoodie")) {
      return "Hoodie";
    }
    if (sizeValue.includes("sweatshirt")) {
      return "Sweatshirt";
    }
    if (sizeValue.includes("polo")) {
      return "Polo";
    }
    if (sizeValue.includes("long")) {
      return "Long Sleeve";
    }
    if (sizeValue.includes("tee") || sizeValue.includes("t-shirt")) {
      return "Tshirt";
    }
  }

  // Sonra combined text'te ara
  for (const { pattern, type } of productPatterns) {
    if (pattern.test(combinedText)) {
      return type;
    }
  }

  const rawValue = getSizeValue(item.options) || item.name || "Unknown Product";
  return rawValue + " (No Match)";
};

// 🆕 Color Tespit - KAPSAMLI VERSİYON
const detectColor = (item) => {
  if (!item?.options) {
    return "white";
  }

  const options = item.options;

  const colorOption = options.find((opt) => {
    const optName = normalizeText(opt.name);
    return (
      optName.includes("color") ||
      optName.includes("colour") ||
      optName.includes("renk") ||
      optName.includes("colorway") ||
      optName.includes("couleur")
    );
  });

  let rawColor = (colorOption?.value || "white").toLowerCase().trim();

  if (
    !rawColor ||
    rawColor === "" ||
    rawColor === "not requested on this item"
  ) {
    return "white";
  }

  // ✅ "/" karakteri varsa sadece ilk kısmı al (örn: "Sport Grey/ Athletic Heather" → "Sport Grey")
  if (rawColor.includes("/")) {
    rawColor = rawColor.split("/")[0].trim();
  }

  // ✅ Parantez içindeki metinleri temizle (örn: "Ivory (Comfort Color Brand)" → "Ivory")
  rawColor = rawColor.replace(/\([^)]*\)/g, "").trim();

  // ✅ "Comfort Colors" marka adını temizle (örn: "Comfort Colors Pepper" → "Pepper")
  rawColor = rawColor.replace(/\bcomfort\s+colors?\b/gi, "").trim();

  // ÖNEMLİ: gray/grey düzeltmesi - tüm gray'leri grey yap
  let normalizedInput = rawColor
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/gray/g, "grey"); // gray -> grey dönüşümü

  // Database mapping - database'deki GERÇEK renk isimlerine göre
  const colorMap = {
    // Database'de "dark gray heather" var, o yüzden grey değil gray kullan
    dark_grey_heather: "dark_gray_heather", // grey -> gray
    dark_gray_heather: "dark_gray_heather",

    dark_grey: "dark_gray", // grey -> gray
    dark_gray: "dark_gray",

    grey_heather: "sport_athletic_heather", // grey -> athletic heather
    gray_heather: "sport_athletic_heather",
    sport_athletic_heather: "sport_athletic_heather",
    sport_grey_heather: "sport_athletic_heather",
    sport_gray_heather: "sport_athletic_heather",
    sport_grey: "sport_athletic_heather", // Sport Grey → Sport Athletic Heather
    sport_gray: "sport_athletic_heather", // Sport Gray → Sport Athletic Heather

    // Diğer renkler
    heather_deep_teal: "heather_deep_teal",
    heather_grass_green: "heather_grass_green",
    heather_maroon: "heather_maroon",
    heather_mauve: "heather_mauve",
    heather_midnigh_navy: "heather_midnigh_navy",
    heather_military_green: "heather_military_green",
    forest_green: "forest_green",
    kelly_green: "kelly_green",
    military_green: "military_green",
    millitary_green: "millitary_green",
    light_pink: "light_pink",
    pink: "light_pink", // Pink → Light Pink mapping (DB'de "Light Pink" olarak kayıtlı olabilir)
    light_green: "light_green",
    dark_chocolate: "dark_chocolate",
    dark_cholate: "dark_chocolate", // Yazım hatası düzeltmesi: cholate -> chocolate
    chocolate: "dark_chocolate", // Sadece "Chocolate" varsa
    cholate: "dark_chocolate", // Sadece "Cholate" varsa
    true_royal: "true_royal",
    royal_blue: "true_royal", // Royal Blue → True Royal mapping
    blue_jean: "blue_jean",
    blue_spruce: "blue_spruce",
    red: "red",
    black: "black",
    white: "white",
    navy: "navy",
  };

  // Önce tam eşleşme
  if (colorMap[normalizedInput]) {
    return colorMap[normalizedInput];
  }

  // Kısmi eşleşme
  for (const [key, value] of Object.entries(colorMap)) {
    if (normalizedInput.includes(key)) {
      return value;
    }
  }

  // ✅ Özel durum: "cholate" veya "chocolate" içeren renkler
  if (
    normalizedInput.includes("cholate") ||
    normalizedInput.includes("chocolate")
  ) {
    return "dark_chocolate";
  }

  // ✅ Eşleşme bulunamadı, normalize edilmiş değeri döndür
  // Backend'den gelen renklerle normalize edilmiş karşılaştırma yapılacak
  return normalizedInput;
};

// 🆕 Normalize fonksiyonu (backend ile aynı mantık)
const flexibleNormalize = (str) => {
  if (!str) return "";

  let normalized = str.toLowerCase().trim();

  // ✅ "/" karakteri varsa sadece ilk kısmı al
  if (normalized.includes("/")) {
    normalized = normalized.split("/")[0].trim();
  }

  // ✅ Parantez içindeki metinleri temizle
  normalized = normalized.replace(/\([^)]*\)/g, "").trim();

  // ✅ "Comfort Colors" marka adını temizle
  normalized = normalized.replace(/\bcomfort\s+colors?\b/gi, "").trim();

  // ✅ Boşlukları ve özel karakterleri normalize et
  normalized = normalized
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/[^\w_]/g, "") // Özel karakterleri temizle
    .replace(/gray/g, "grey"); // gray -> grey dönüşümü (detectColor ile tutarlılık için)

  return normalized;
};

// 🆕 Filtreleme fonksiyonları
const getFilteredColors = (
  itemValidations,
  itemLineItemKey,
  mockupCombinations,
  availableColors
) => {
  const validation = itemValidations[itemLineItemKey];
  if (!validation || !mockupCombinations.length) return availableColors;

  const normalizedProductType = flexibleNormalize(validation.productType);

  // Eğer product type seçilmişse, sadece o product type'ın renklerini göster
  if (validation.productType) {
    const validColors = mockupCombinations
      .filter(
        (combo) =>
          flexibleNormalize(combo.product_type) === normalizedProductType
      )
      .map((combo) => combo.color);
    const uniqueColors = [...new Set(validColors)].sort();
    return uniqueColors.length > 0 ? uniqueColors : availableColors;
  }

  return availableColors;
};

const getFilteredProductTypes = (
  itemValidations,
  itemLineItemKey,
  mockupCombinations,
  availableProductTypes
) => {
  const validation = itemValidations[itemLineItemKey];
  if (!validation || !mockupCombinations.length) return availableProductTypes;

  const normalizedColor = flexibleNormalize(validation.color);

  // Eğer renk seçilmişse, sadece o rengin product type'larını göster
  if (validation.color) {
    const validProductTypes = mockupCombinations
      .filter((combo) => flexibleNormalize(combo.color) === normalizedColor)
      .map((combo) => combo.product_type);
    const uniqueProductTypes = [...new Set(validProductTypes)].sort();
    return uniqueProductTypes.length > 0
      ? uniqueProductTypes
      : availableProductTypes;
  }

  return availableProductTypes;
};

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const ordersList = location.state?.orders || [];
  const userEmail = localStorage.getItem("email");
  const isEmbroideryUser = userEmail === "embroidery@txmaya.com";
  const [showUploadModal, setShowUploadModal] = useState(false);
  const currentIndex = ordersList.findIndex(
    (o) => String(o.orderId) === String(orderId)
  );

  const prevOrderId =
    currentIndex > 0 ? ordersList[currentIndex - 1]?.orderId : null;
  const nextOrderId =
    currentIndex >= 0 && currentIndex < ordersList.length - 1
      ? ordersList[currentIndex + 1]?.orderId
      : null;

  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState({});
  const [saving, setSaving] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const [editOptionState, setEditOptionState] = useState({});
  const [editingAddress, setEditingAddress] = useState(false);
  const [shipToDraft, setShipToDraft] = useState({});
  const [tags, setTags] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // Yeni state: copy butonunun aktif olup olmadığını kontrol eder
  const [isCopyEnabled, setIsCopyEnabled] = useState(false);
  // Yeni state: tag loading durumu
  const [tagLoading, setTagLoading] = useState(false);

  // 🆕 Validation state'leri
  const [availableProductTypes, setAvailableProductTypes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [mockupCombinations, setMockupCombinations] = useState([]);

  // 🔒 Ref kullanarak lock mekanizması - re-render'da kaybolmaz
  const lockedValidationsRef = useRef({});

  // localStorage'dan validation'ları yükle
  const getStoredValidations = () => {
    try {
      const stored = localStorage.getItem(`itemValidations_${orderId}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const [itemValidations, setItemValidations] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [searchText, setSearchText] = useState({});

  // 🆕 Component yüklendiğinde available options'ı al
  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_mockup_by_product.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          setAvailableProductTypes(res.data.product_types || []);
          setAvailableColors(res.data.colors || []);
          setMockupCombinations(res.data.combinations || []);
        }
      })
      .catch((err) => {
        console.error("❌ Error fetching options:", err);
      });
  }, []);

  // 🆕 Order değiştiğinde item validationları yap
  useEffect(() => {
    if (!order || !order.items || availableProductTypes.length === 0) return;

    const validations = {};

    order.items.forEach((item) => {
      // Ref'ten locked validation'ı kontrol et
      const lockedValidation = lockedValidationsRef.current[item.lineItemKey];

      if (lockedValidation) {
        // Locked validation var, onu kullan
        validations[item.lineItemKey] = lockedValidation;
        return;
      }

      // Yeni item veya lock değil, tespit yap
      const productType = detectProductType(item);
      const color = detectColor(item);

      // ✅ Normalize edilmiş karşılaştırma yap
      const normalizedProductType = flexibleNormalize(productType);
      const normalizedColor = flexibleNormalize(color);

      const productTypeValid = availableProductTypes.some(
        (pt) => flexibleNormalize(pt) === normalizedProductType
      );
      const colorValid = availableColors.some(
        (c) => flexibleNormalize(c) === normalizedColor
      );

      const rawProductType = getSizeValue(item.options) || item.name;
      const rawColor = getColorValue(item.options);

      validations[item.lineItemKey] = {
        productType,
        productTypeDisplay: productTypeValid
          ? rawProductType
          : `${rawProductType} (No Match)`,
        color,
        colorDisplay: colorValid ? rawColor : `${rawColor} (No Match)`,
        productTypeValid,
        colorValid,
      };
    });

    setItemValidations(validations);
  }, [order, availableProductTypes, availableColors]);

  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_shipstation_tags.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        setTags(res.data.tags || []);
      })
      .catch((err) => {
        setTags([]);
      });
  }, []);

  // 🆕 Dropdown dışına tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Dropdown veya badge'e tıklanmadıysa kapat
      const isDropdownClick = event.target.closest("[data-dropdown]");
      const isBadgeClick = event.target.closest("[data-badge]");

      if (!isDropdownClick && !isBadgeClick) {
        setDropdownOpen({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchOrderDetails = () => {
    setLoading(true);
    setIsCopyEnabled(false); // Yeni order yüklenirken copy butonunu devre dışı bırak
    axios
      .get(
        `${settings.apiURL}/get_order_details_from_db.php?orderId=${orderId}`,
        { headers: authHeader() }
      )
      .then((res) => {
        setOrder(res.data.order);
        setEditNotes({
          internalNote: res.data.order.internalNotes || "",
          customerNotes: res.data.order.customerNotes || "",
        });
        setLoading(false);
        // Order tam yüklendiğinde copy butonunu aktif et
        setTimeout(() => {
          setIsCopyEnabled(true);
        }, 100);
      })
      .catch((err) => {
        setLoading(false);
        setIsCopyEnabled(false); // Hata durumunda da devre dışı bırak
      });
  };

  useEffect(() => {
    // OrderId değiştiğinde lockedValidations'ı localStorage'dan yükle
    const stored = getStoredValidations();
    lockedValidationsRef.current = stored;

    fetchOrderDetails();
  }, [orderId]);

  useEffect(() => {
    setEditOptionState({});
    setNotesChanged(false);
    setShipToDraft({});
    setIsCopyEnabled(false); // Order değiştiğinde copy butonunu devre dışı bırak
  }, [orderId]);

  // Kopyalama fonksiyonu - 0 AMZ için özel format
  const handleCopy = (item) => {
    if (!isCopyEnabled || !order) {
      toast("Please wait for order to load completely!", "warning");
      return;
    }

    const shopName = order.shopName || "Shop Name";
    const customerName = order.shipTo?.name || "Customer";
    const orderNumber = `#${order.orderNumber}`;
    const firstName = localStorage.getItem("first_name") || "";
    const lastName = localStorage.getItem("last_name") || "";
    const userName = `${firstName} ${lastName}`.trim() || "User";

    const cleanOptions =
      item.options?.filter(
        (opt) => !normalizeText(opt.name).includes("personalization")
      ) || [];
    const size = getSizeValue(cleanOptions);
    const color = getColorValue(cleanOptions);

    // Personalization bilgisini al
    const personalizationOption = item.options?.find((opt) =>
      normalizeText(opt.name).includes("personalization")
    );
    const personalizationValue = personalizationOption?.value?.trim();

    let textToCopy = "";

    // 0 AMZ mağazası için özel format
    if (shopName === "0 AMZ" || shopName === "0 VILLA") {
      // SKU'dan RET önekini kaldır
      let cleanedSku = item.sku || "";
      if (cleanedSku.startsWith("RET")) {
        cleanedSku = cleanedSku.substring(3).trim();
      }

      // Tireleri temizle ve parçalara ayır
      const parts = [size, color, "Gildan Heavy Cotton Tshirt", cleanedSku]
        .filter((part) => part && part !== "-" && part.trim() !== "")
        .map((part) => part.trim());

      textToCopy = `${shopName} - ${orderNumber} - ${customerName} - ${userName}
${parts.join(" - ")} - quantity: ${item.quantity}`;
    } else {
      // Diğer mağazalar için normal format
      textToCopy = `${shopName} - ${orderNumber} - ${customerName} - ${userName}
${size} - ${color} - quantity: ${item.quantity}`;
    }

    // Personalization varsa ekle
    if (personalizationValue) {
      textToCopy += `\nPersonalization: ${personalizationValue}`;
    }

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => toast("Copied to clipboard!", "success"))
      .catch(() => toast("Copy failed!", "error"));
  };

  const handleCopyBarcode = async () => {
    if (!isCopyEnabled || !order) {
      toast("Please wait for order to load completely!", "warning");
      return;
    }

    const orderNumber = order.orderNumber;
    if (!orderNumber) {
      toast("Order number not found!", "error");
      return;
    }

    const barcodeText = `*${orderNumber}*`;
    const barcodeFont = '50px "Libre Barcode 39"';
    const labelFont = "12px Arial";
    const padding = 4; // kenar boşluğu
    const spacing = 2; // barkod ile yazı arası

    try {
      await document.fonts.load(barcodeFont);
      await document.fonts.ready;

      // Ölçüm için 1x ölçekli canvas
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");
      tempCtx.font = barcodeFont;
      const barcodeMetrics = tempCtx.measureText(barcodeText);
      tempCtx.font = labelFont;
      const labelMetrics = tempCtx.measureText(orderNumber);

      const barcodeHeight = Math.ceil(
        barcodeMetrics.actualBoundingBoxAscent +
          barcodeMetrics.actualBoundingBoxDescent
      );
      const labelHeight = Math.ceil(
        labelMetrics.actualBoundingBoxAscent +
          labelMetrics.actualBoundingBoxDescent
      );

      const contentWidth = Math.ceil(
        Math.max(barcodeMetrics.width, labelMetrics.width)
      );
      const contentHeight = barcodeHeight + spacing + labelHeight;

      const width = contentWidth + padding * 2;
      const height = contentHeight + padding * 2;

      // Illustrator için tam 3.5 inç genişlik
      const scale = 336 / width;

      // Yüksek çözünürlüklü canvas
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");

      // Ölçekle
      ctx.scale(scale, scale);

      // Arka plan
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, width, height);

      // Barkod
      ctx.font = barcodeFont;
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(barcodeText, width / 2, padding);

      // Alt yazı (yakın ama temas etmiyor)
      ctx.font = labelFont;
      ctx.textBaseline = "top";
      ctx.fillText(orderNumber, width / 2, padding + barcodeHeight + spacing);

      // Görseli kopyala
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
            }),
          ]);
          toast("Barcode copied at exactly 3.5 inch width!", "success");
        } catch (err) {
          await navigator.clipboard.writeText(barcodeText);
          toast(
            "Fallback: barcode text copied. Use Libre Barcode 39 font.",
            "success"
          );
        }
      });
    } catch (err) {
      try {
        await navigator.clipboard.writeText(barcodeText);
        toast(
          "Fallback: barcode text copied. Use Libre Barcode 39 font.",
          "success"
        );
      } catch (fallbackErr) {
        toast("Copy failed!", "error");
      }
    }
  };

  const handleNoteChange = (field, value) => {
    setEditNotes((prev) => ({ ...prev, [field]: value }));
    setNotesChanged(true);
  };

  const startEditingAddress = () => {
    setShipToDraft(order.shipTo || {});
    setEditingAddress(true);
  };

  const cancelEditingAddress = () => {
    setEditingAddress(false);
  };

  const handleAddressChange = (field, value) => {
    setShipToDraft((prev) => ({ ...prev, [field]: value }));
  };

  const saveAddress = () => {
    axios
      .post(
        `${settings.apiURL}/update_shipping_address.php`,
        { orderId: order.orderId, shipTo: shipToDraft },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Address updated!", "success");
          setOrder((prev) => ({ ...prev, shipTo: shipToDraft }));
          setEditingAddress(false);
        } else {
          toast("Failed to update address", "error");
        }
      })
      .catch(() => {
        toast("Error updating address", "error");
      });
  };

  const saveNotes = () => {
    if (!notesChanged) return;
    setSaving(true);
    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId, ...editNotes },
        { headers: authHeader() }
      )
      .then(() => {
        setSaving(false);
        setNotesChanged(false);
        toast("Notes updated successfully!", "success");
      })
      .catch(() => {
        setSaving(false);
        toast("Failed to update notes!", "error");
      });
  };

  const handleSaveOption = (
    orderId,
    lineItemKey,
    optionName,
    newValue,
    key
  ) => {
    axios
      .post(
        `${settings.apiURL}/update_item_options.php`,
        {
          orderId,
          lineItemKey,
          optionName,
          newValue,
        },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast("Option updated successfully!", "success");
          setEditOptionState((prev) => ({
            ...prev,
            [key]: { editing: false, value: newValue },
          }));

          // ✅ Frontend'de anında güncelle
          setOrder((prevOrder) => {
            const updatedItems = prevOrder.items.map((item) => {
              if (item.lineItemKey === lineItemKey) {
                const newOptions = item.options.map((opt) => {
                  if (opt.name.toLowerCase() === optionName.toLowerCase()) {
                    return { ...opt, value: newValue };
                  }
                  return opt;
                });
                return { ...item, options: newOptions };
              }
              return item;
            });
            return { ...prevOrder, items: updatedItems };
          });
        } else {
          toast(res.data.message || "Update failed!", "error");
        }
      })
      .catch((err) => {
        toast("Update failed!", "error");
      });
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-US") : "-";

  if (loading) return <p>Loading...</p>;
  if (!order) return <p>Order not found.</p>;

  const itemsFiltered =
    order.items?.filter(
      (item) => !["discount", "shipping"].includes(item.name.toLowerCase())
    ) || [];
  const discountItem = order.items?.find(
    (item) => item.name.toLowerCase() === "discount"
  );
  const discountAmount = discountItem
    ? -Math.abs(discountItem.unitPrice * discountItem.quantity)
    : 0;

  const sectionStyle = {
    border: "1px solid #ccc",
    padding: "15px",
    marginBottom: "15px",
    borderRadius: "5px",
  };
  const tableStyle = {
    width: "100%",
    fontSize: "13px",
    borderCollapse: "collapse",
  };
  const cellStyle = { border: "1px solid #ccc", padding: "8px" };
  const thumbnailStyle = {
    width: "50px",
    transition: "transform 0.3s ease",
    cursor: "pointer",
  };

  const createTrackingUrl = (tracking) => {
    if (tracking.trackingUrl) return tracking.trackingUrl;

    const trackingNumber = tracking.trackingNumber;
    const carrier = tracking.carrierCode?.toLowerCase() || "";

    // UPS için (ups_walleted, ups_ground, ups_2nd_day_air, ups_next_day_air, vs)
    if (carrier.includes("ups")) {
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    }

    // USPS için (usps, usps_first_class_mail, usps_priority_mail, vs)
    if (carrier.includes("usps") || carrier.includes("stamps_com")) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    }

    // FedEx için
    if (carrier.includes("fedex")) {
      return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    }

    // DHL için
    if (carrier.includes("dhl")) {
      return `https://www.dhl.com/us-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${trackingNumber}`;
    }

    // Bilinmeyen carrier için Google arama
    return `https://www.google.com/search?q=${encodeURIComponent(
      trackingNumber + " tracking"
    )}`;
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "20px",
        padding: "20px",
        fontSize: "14px",
        position: "relative",
      }}
    >
      <button
        onClick={() =>
          navigate(isEmbroideryUser ? "/embroidery-my-orders" : "/my-orders")
        }
        style={{
          position: "absolute",
          top: "10px",
          right: "20px",
          fontSize: "20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        ❌
      </button>

      <aside style={{ flex: "0 0 300px" }}>
        <h4>Order Summary</h4>
        <p>
          <strong>Order #</strong>
          <br />
          {order.orderNumber}
        </p>
        <p>
          <strong>Status</strong>
          <br />
          {order.orderStatus || "-"}
        </p>
        <p>
          <strong>Sold To</strong>
          <br />
          {order.shipTo?.name}
          <br />
          {order.customerEmail || "-"}
        </p>
        <p>
          <strong>Ship To Address</strong>
        </p>

        {editingAddress ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <input
              value={shipToDraft.name || ""}
              onChange={(e) => handleAddressChange("name", e.target.value)}
              placeholder="Name"
            />
            <input
              value={shipToDraft.street1 || ""}
              onChange={(e) => handleAddressChange("street1", e.target.value)}
              placeholder="Street"
            />
            <input
              value={shipToDraft.city || ""}
              onChange={(e) => handleAddressChange("city", e.target.value)}
              placeholder="City"
            />
            <input
              value={shipToDraft.state || ""}
              onChange={(e) => handleAddressChange("state", e.target.value)}
              placeholder="State"
            />
            <input
              value={shipToDraft.postalCode || ""}
              onChange={(e) =>
                handleAddressChange("postalCode", e.target.value)
              }
              placeholder="Postal Code"
            />
            <input
              value={shipToDraft.country || ""}
              onChange={(e) => handleAddressChange("country", e.target.value)}
              placeholder="Country"
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <button onClick={saveAddress} className="btn btn-success btn-sm">
                Save
              </button>
              <button
                onClick={cancelEditingAddress}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {order.shipTo?.name}
            <br />
            {order.shipTo?.street1}
            <br />
            {order.shipTo?.city}, {order.shipTo?.state}{" "}
            {order.shipTo?.postalCode}
            <br />
            {order.shipTo?.country}
            <br />
            <button
              onClick={startEditingAddress}
              className="btn btn-link p-0 mt-1"
            >
              Edit Address
            </button>
          </div>
        )}

        <p>
          <strong>Product:</strong> $
          {itemsFiltered
            .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
            .toFixed(2)}
        </p>
        <p>
          <strong>Shipping:</strong> ${order.shippingAmount}
        </p>
        <p>
          <strong>Tax:</strong> ${order.taxAmount}
        </p>
        <p>
          <strong>Order Total:</strong> ${order.orderTotal}
        </p>
        <p>
          <strong>Discount Total:</strong>{" "}
          {discountAmount !== 0 ? `$${discountAmount.toFixed(2)}` : "-$0.00"}
        </p>
        <p>
          <strong>Total Paid:</strong> ${order.amountPaid}
        </p>
        <p>
          <strong>Order Date:</strong> {formatDate(order.createDate)}
        </p>
        <p>
          <strong>Ship By:</strong> {formatDate(order.shipByDate)}
        </p>
        <p>
          <strong>Order Items:</strong> {itemsFiltered.length} items
        </p>
      </aside>

      <main style={{ flex: 1 }}>
        <h3>Shipment Details</h3>
        <div
          style={{
            ...sectionStyle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
          }}
        >
          {/* Left: Ship To Address */}
          <div>
            <p>
              <strong>Ship To Address</strong>
              <br />
              {order.shipTo?.name}
              <br />
              {order.shipTo?.street1}
              <br />
              {order.shipTo?.city}, {order.shipTo?.state}{" "}
              {order.shipTo?.postalCode}
              <br />
              {order.shipTo?.country}
            </p>
            <span
              style={{
                backgroundColor:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#d4edda"
                    : "#f8d7da",
                color:
                  order.shipTo?.addressVerified ===
                  "Address validated successfully"
                    ? "#155724"
                    : "#721c24",
                padding: "4px 8px",
                borderRadius: "6px",
                display: "inline-block",
                marginTop: "5px",
                fontWeight: "bold",
              }}
            >
              {order.shipTo?.addressVerified ===
              "Address validated successfully"
                ? "✅ Address Validated"
                : "⚠️ Address Not Validated"}
            </span>
          </div>

          {/* Right: Tag Dropdown */}
          <div
            style={{
              ...sectionStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            {/* Sol: Tracking Information */}
            <div style={{ flex: 1 }}>
              <h4>Tracking Information</h4>
              {order.trackingInfo && order.trackingInfo.length > 0 ? (
                order.trackingInfo.map((tracking, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "10px",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                    }}
                  >
                    <strong>Tracking #{index + 1}:</strong>
                    <br />
                    <strong>Number:</strong> {tracking.trackingNumber}
                    <br />
                    <strong>Link:</strong>{" "}
                    <a
                      href={createTrackingUrl(tracking)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#007bff",
                        textDecoration: "underline",
                      }}
                    >
                      📦 Track Package ({tracking.carrierCode || "Unknown"})
                    </a>
                    <br />
                    <strong>Carrier:</strong>{" "}
                    {tracking.carrierCode || "Unknown"}
                    <br />
                    {tracking.shipDate && (
                      <>
                        <strong>Ship Date:</strong>{" "}
                        {formatDate(tracking.shipDate)}
                      </>
                    )}
                  </div>
                ))
              ) : (
                <p>No tracking information available</p>
              )}
            </div>

            {/* Sağ: Tag Dropdown */}
          </div>
          <div style={{ minWidth: "180px", position: "relative" }}>
            <label style={{ fontWeight: "bold" }}>Tag:</label>
            <select
              className="form-select form-select-sm"
              value={order.tagIds?.[0] || ""}
              disabled={tagLoading}
              onChange={(e) => {
                const selectedTagId = e.target.value;
                setTagLoading(true);

                const payload =
                  selectedTagId === ""
                    ? {
                        orderNumber: order.orderNumber,
                        orderId: order.orderId,
                        tagId: "remove",
                        removeTagId: order.tagIds?.[0],
                      }
                    : {
                        orderNumber: order.orderNumber,
                        orderId: order.orderId,
                        tagId: selectedTagId,
                      };

                axios
                  .post(`${settings.apiURL}/update_order_tag.php`, payload, {
                    headers: authHeader(),
                  })
                  .then((res) => {
                    if (res.data.success) {
                      toast(res.data.message, "success");
                      setOrder((prevOrder) => ({
                        ...prevOrder,
                        tagIds: selectedTagId === "" ? [] : [selectedTagId],
                      }));
                    } else {
                      toast("Failed to update tag!", "error");
                    }
                  })
                  .catch((err) => {
                    toast("Tag update failed!", "error");
                  })
                  .finally(() => {
                    setTagLoading(false);
                  });
              }}
            >
              <option value="">No tag</option>
              {tags.map((tag) => (
                <option key={tag.tagId} value={tag.tagId}>
                  {tag.name}
                </option>
              ))}
            </select>
            {tagLoading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "30px",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
              </div>
            )}
          </div>
        </div>

        <div style={sectionStyle}>
          <h4>Shipment Items</h4>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={cellStyle}>Image</th>
                <th style={cellStyle}>Item Name</th>
                <th style={cellStyle}>SKU</th>
                <th style={cellStyle}>Unit Cost</th>
                <th style={cellStyle}>Quantity</th>
                <th style={cellStyle}>Info</th>
              </tr>
            </thead>
            <tbody>
              {itemsFiltered.map((item, i) => {
                const isDTFOrder = !item.options?.some((opt) => {
                  const name = normalizeText(opt.name);
                  return name.includes("size") || name.includes("color");
                });

                return (
                  <tr key={i}>
                    <td style={cellStyle}>
                      <img
                        src={item.imageUrl}
                        key={item.lineItemKey}
                        alt=""
                        style={thumbnailStyle}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.transform = "scale(5)")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      />
                    </td>
                    <td style={cellStyle}>
                      <div style={{ marginBottom: "4px" }}>
                        <strong>Item:</strong> {item.lineItemKey}
                      </div>
                      {item.name}
                      <br />
                      {item.options
                        ?.filter((opt) => {
                          const name = normalizeText(opt.name);
                          return (
                            name.includes("size") || name.includes("color")
                          );
                        })
                        .map((opt, j) => {
                          const key = `${i}-${j}`;
                          const isEditing =
                            editOptionState[key]?.editing || false;
                          const tempValue =
                            editOptionState[key]?.value ?? opt.value;

                          // Capitalize first letter
                          const displayName =
                            opt.name.charAt(0).toUpperCase() +
                            opt.name.slice(1);

                          return (
                            <div key={j} style={{ marginTop: "5px" }}>
                              <strong>{displayName}:</strong>{" "}
                              {isEditing ? (
                                <>
                                  <input
                                    type="text"
                                    value={tempValue}
                                    onChange={(e) =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...prev[key],
                                          value: e.target.value,
                                        },
                                      }))
                                    }
                                    style={{ width: "150px", fontSize: "12px" }}
                                  />
                                  <button
                                    style={{
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      handleSaveOption(
                                        order.orderId,
                                        item.lineItemKey,
                                        opt.name,
                                        tempValue,
                                        key
                                      )
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    style={{
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          ...prev[key],
                                          editing: false,
                                          value: opt.value, // Eski değeri geri getiriyoruz
                                        },
                                      }))
                                    }
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {opt.value}
                                  <span
                                    style={{
                                      color: "blue",
                                      cursor: "pointer",
                                      marginLeft: "5px",
                                      fontSize: "12px",
                                    }}
                                    onClick={() =>
                                      setEditOptionState((prev) => ({
                                        ...prev,
                                        [key]: {
                                          editing: true,
                                          value: opt.value,
                                        },
                                      }))
                                    }
                                  >
                                    Edit
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        })}

                      {/* 🆕 VALIDATION ALANLARI - Modern & Compact */}
                      {itemValidations[item.lineItemKey] && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "6px 8px",
                            backgroundColor: "#f8fafc",
                            borderRadius: "6px",
                            border: "1px solid #e2e8f0",
                            width: "fit-content",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "9px",
                              color: "#64748b",
                              fontWeight: "600",
                              marginBottom: "6px",
                              letterSpacing: "0.5px",
                              textTransform: "uppercase",
                            }}
                          >
                            Mockup Validation
                          </div>
                          <div
                            style={{
                              display: "inline-flex",
                              gap: "6px",
                              alignItems: "center",
                            }}
                          >
                            {/* Product Type Badge */}
                            <div style={{ position: "relative" }}>
                              <div
                                data-badge="product-type"
                                onClick={() => {
                                  const key = `${item.lineItemKey}-pt`;
                                  setDropdownOpen({
                                    [key]: !dropdownOpen[key],
                                  });
                                }}
                                style={{
                                  padding: "4px 8px",
                                  border: `1.5px solid ${
                                    itemValidations[item.lineItemKey]
                                      .productTypeValid
                                      ? "#22c55e"
                                      : "#ef4444"
                                  }`,
                                  backgroundColor: itemValidations[
                                    item.lineItemKey
                                  ].productTypeValid
                                    ? "#f0fdf4"
                                    : "#fef2f2",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  textAlign: "center",
                                  width: "auto",
                                  maxWidth: "200px",
                                  transition: "all 0.2s ease",
                                  boxShadow: itemValidations[item.lineItemKey]
                                    .productTypeValid
                                    ? "0 1px 3px rgba(34, 197, 94, 0.2)"
                                    : "0 1px 3px rgba(239, 68, 68, 0.2)",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    !itemValidations[item.lineItemKey]
                                      .productTypeValid
                                  ) {
                                    e.target.style.transform =
                                      "translateY(-1px)";
                                    e.target.style.boxShadow =
                                      "0 2px 6px rgba(239, 68, 68, 0.3)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = itemValidations[
                                    item.lineItemKey
                                  ].productTypeValid
                                    ? "0 1px 3px rgba(34, 197, 94, 0.2)"
                                    : "0 1px 3px rgba(239, 68, 68, 0.2)";
                                }}
                                title={
                                  itemValidations[item.lineItemKey]
                                    ?.productTypeDisplay
                                }
                              >
                                {itemValidations[item.lineItemKey]
                                  .productTypeValid
                                  ? "✓"
                                  : "✗"}{" "}
                                {
                                  itemValidations[item.lineItemKey]
                                    ?.productTypeDisplay
                                }
                              </div>

                              {dropdownOpen[`${item.lineItemKey}-pt`] && (
                                <div
                                  data-dropdown="product-type"
                                  style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    minWidth: "180px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                    zIndex: 1001,
                                    boxShadow:
                                      "0 10px 25px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)",
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="🔍 Search..."
                                    value={
                                      searchText[`${item.lineItemKey}-pt`] || ""
                                    }
                                    onChange={(e) =>
                                      setSearchText((prev) => ({
                                        ...prev,
                                        [`${item.lineItemKey}-pt`]:
                                          e.target.value,
                                      }))
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "10px 12px",
                                      border: "none",
                                      borderBottom: "1px solid #e5e7eb",
                                      outline: "none",
                                      fontSize: "12px",
                                      boxSizing: "border-box",
                                    }}
                                    autoFocus
                                  />
                                  <div
                                    style={{
                                      maxHeight: "160px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {(() => {
                                      const filteredByMockup =
                                        getFilteredProductTypes(
                                          itemValidations,
                                          item.lineItemKey,
                                          mockupCombinations,
                                          availableProductTypes
                                        );
                                      const filtered = filteredByMockup.filter(
                                        (pt) =>
                                          pt
                                            .toLowerCase()
                                            .includes(
                                              (
                                                searchText[
                                                  `${item.lineItemKey}-pt`
                                                ] || ""
                                              ).toLowerCase()
                                            )
                                      );

                                      if (filtered.length === 0) {
                                        return (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#94a3b8",
                                              fontSize: "11px",
                                            }}
                                          >
                                            No matches found
                                          </div>
                                        );
                                      }

                                      return filtered.map((pt, idx) => (
                                        <div
                                          key={idx}
                                          onClick={() => {
                                            const newValidation = {
                                              ...itemValidations[
                                                item.lineItemKey
                                              ],
                                              productType: pt,
                                              productTypeDisplay: pt,
                                              productTypeValid: true,
                                            };

                                            // Ref'e kaydet (kalıcı)
                                            lockedValidationsRef.current[
                                              item.lineItemKey
                                            ] = newValidation;

                                            // localStorage'a kaydet
                                            localStorage.setItem(
                                              `itemValidations_${orderId}`,
                                              JSON.stringify(
                                                lockedValidationsRef.current
                                              )
                                            );

                                            // State'i güncelle (UI için)
                                            setItemValidations((prev) => ({
                                              ...prev,
                                              [item.lineItemKey]: newValidation,
                                            }));

                                            setDropdownOpen({});
                                            setSearchText({});
                                          }}
                                          style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            borderBottom:
                                              idx < filtered.length - 1
                                                ? "1px solid #f3f4f6"
                                                : "none",
                                            transition: "all 0.15s ease",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor =
                                              "#f0fdf4";
                                            e.target.style.paddingLeft = "16px";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor =
                                              "transparent";
                                            e.target.style.paddingLeft = "12px";
                                          }}
                                        >
                                          {pt}
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Color Badge */}
                            <div style={{ position: "relative" }}>
                              <div
                                data-badge="color"
                                onClick={() => {
                                  const key = `${item.lineItemKey}-color`;
                                  setDropdownOpen({
                                    [key]: !dropdownOpen[key],
                                  });
                                }}
                                style={{
                                  padding: "4px 8px",
                                  border: `1.5px solid ${
                                    itemValidations[item.lineItemKey].colorValid
                                      ? "#22c55e"
                                      : "#ef4444"
                                  }`,
                                  backgroundColor: itemValidations[
                                    item.lineItemKey
                                  ].colorValid
                                    ? "#f0fdf4"
                                    : "#fef2f2",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  textAlign: "center",
                                  width: "auto",
                                  maxWidth: "200px",
                                  transition: "all 0.2s ease",
                                  boxShadow: itemValidations[item.lineItemKey]
                                    .colorValid
                                    ? "0 1px 3px rgba(34, 197, 94, 0.2)"
                                    : "0 1px 3px rgba(239, 68, 68, 0.2)",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onMouseEnter={(e) => {
                                  if (
                                    !itemValidations[item.lineItemKey]
                                      .colorValid
                                  ) {
                                    e.target.style.transform =
                                      "translateY(-1px)";
                                    e.target.style.boxShadow =
                                      "0 2px 6px rgba(239, 68, 68, 0.3)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "translateY(0)";
                                  e.target.style.boxShadow = itemValidations[
                                    item.lineItemKey
                                  ].colorValid
                                    ? "0 1px 3px rgba(34, 197, 94, 0.2)"
                                    : "0 1px 3px rgba(239, 68, 68, 0.2)";
                                }}
                                title={
                                  itemValidations[item.lineItemKey]
                                    ?.colorDisplay
                                }
                              >
                                {itemValidations[item.lineItemKey].colorValid
                                  ? "✓"
                                  : "✗"}{" "}
                                {
                                  itemValidations[item.lineItemKey]
                                    ?.colorDisplay
                                }
                              </div>

                              {dropdownOpen[`${item.lineItemKey}-color`] && (
                                <div
                                  data-dropdown="color"
                                  style={{
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    minWidth: "200px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                    zIndex: 1001,
                                    boxShadow:
                                      "0 10px 25px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)",
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="🔍 Search..."
                                    value={
                                      searchText[`${item.lineItemKey}-color`] ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      setSearchText((prev) => ({
                                        ...prev,
                                        [`${item.lineItemKey}-color`]:
                                          e.target.value,
                                      }))
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "10px 12px",
                                      border: "none",
                                      borderBottom: "1px solid #e5e7eb",
                                      outline: "none",
                                      fontSize: "12px",
                                      boxSizing: "border-box",
                                    }}
                                    autoFocus
                                  />
                                  <div
                                    style={{
                                      maxHeight: "160px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {(() => {
                                      const filteredByMockup =
                                        getFilteredColors(
                                          itemValidations,
                                          item.lineItemKey,
                                          mockupCombinations,
                                          availableColors
                                        );
                                      const filtered = filteredByMockup.filter(
                                        (c) =>
                                          c
                                            .toLowerCase()
                                            .includes(
                                              (
                                                searchText[
                                                  `${item.lineItemKey}-color`
                                                ] || ""
                                              ).toLowerCase()
                                            )
                                      );

                                      if (filtered.length === 0) {
                                        return (
                                          <div
                                            style={{
                                              padding: "16px",
                                              textAlign: "center",
                                              color: "#94a3b8",
                                              fontSize: "11px",
                                            }}
                                          >
                                            No matches found
                                          </div>
                                        );
                                      }

                                      return filtered.map((c, idx) => (
                                        <div
                                          key={idx}
                                          onClick={() => {
                                            const newValidation = {
                                              ...itemValidations[
                                                item.lineItemKey
                                              ],
                                              color: c,
                                              colorDisplay: c,
                                              colorValid: true,
                                            };

                                            // Ref'e kaydet (kalıcı)
                                            lockedValidationsRef.current[
                                              item.lineItemKey
                                            ] = newValidation;

                                            // localStorage'a kaydet
                                            localStorage.setItem(
                                              `itemValidations_${orderId}`,
                                              JSON.stringify(
                                                lockedValidationsRef.current
                                              )
                                            );

                                            // State'i güncelle (UI için)
                                            setItemValidations((prev) => ({
                                              ...prev,
                                              [item.lineItemKey]: newValidation,
                                            }));

                                            setDropdownOpen({});
                                            setSearchText({});
                                          }}
                                          style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            borderBottom:
                                              idx < filtered.length - 1
                                                ? "1px solid #f3f4f6"
                                                : "none",
                                            transition: "all 0.15s ease",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor =
                                              "#f0fdf4";
                                            e.target.style.paddingLeft = "16px";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor =
                                              "transparent";
                                            e.target.style.paddingLeft = "12px";
                                          }}
                                        >
                                          {c}
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{ marginTop: "8px" }}>
                        <strong>Personalization:</strong>{" "}
                        {item.options?.find(
                          (opt) => normalizeText(opt.name) === "personalization"
                        )?.value || "Not requested on this item."}
                        {order.shopName === "0 AMZ" && (
                          <div style={{ marginTop: "5px" }}>
                            <strong>Amazon Order Link:</strong>{" "}
                            <a
                              href={`https://sellercentral.amazon.com/orders-v3/order/${order.orderNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "blue",
                                textDecoration: "underline",
                              }}
                            >
                              View Order
                            </a>
                          </div>
                        )}
                        {(() => {
                          const targetNames = [
                            "dimension",
                            "dimensions",
                            "style",
                            "size",
                            "beden",
                          ];
                          const dimensionOption = item.options?.find((opt) =>
                            targetNames.includes(normalizeText(opt.name))
                          );
                          return dimensionOption ? (
                            <div style={{ marginTop: "5px" }}>
                              <strong>Dimensions:</strong>{" "}
                              {dimensionOption.value}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      <button
                        onClick={() => {
                          const validation = itemValidations[item.lineItemKey];
                          if (
                            validation &&
                            (!validation.productTypeValid ||
                              !validation.colorValid)
                          ) {
                            toast(
                              "Please fix product type and color validation first!",
                              "warning"
                            );
                            return;
                          }
                          setSelectedItem(item);
                          setShowUploadModal(true);
                        }}
                        style={{
                          backgroundColor:
                            itemValidations[item.lineItemKey] &&
                            (!itemValidations[item.lineItemKey]
                              .productTypeValid ||
                              !itemValidations[item.lineItemKey].colorValid)
                              ? "#cbd5e1"
                              : "#f59e0b",
                          color:
                            itemValidations[item.lineItemKey] &&
                            (!itemValidations[item.lineItemKey]
                              .productTypeValid ||
                              !itemValidations[item.lineItemKey].colorValid)
                              ? "#64748b"
                              : "white",
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: "8px",
                          boxShadow:
                            itemValidations[item.lineItemKey] &&
                            (!itemValidations[item.lineItemKey]
                              .productTypeValid ||
                              !itemValidations[item.lineItemKey].colorValid)
                              ? "0 1px 3px rgba(0, 0, 0, 0.08)"
                              : "0 2px 8px rgba(245, 158, 11, 0.3)",
                          fontWeight: "600",
                          fontSize: "13px",
                          cursor:
                            itemValidations[item.lineItemKey] &&
                            (!itemValidations[item.lineItemKey]
                              .productTypeValid ||
                              !itemValidations[item.lineItemKey].colorValid)
                              ? "not-allowed"
                              : "pointer",
                          marginTop: "10px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (
                            !(
                              itemValidations[item.lineItemKey] &&
                              (!itemValidations[item.lineItemKey]
                                .productTypeValid ||
                                !itemValidations[item.lineItemKey].colorValid)
                            )
                          ) {
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(245, 158, 11, 0.4)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          if (
                            !(
                              itemValidations[item.lineItemKey] &&
                              (!itemValidations[item.lineItemKey]
                                .productTypeValid ||
                                !itemValidations[item.lineItemKey].colorValid)
                            )
                          ) {
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(245, 158, 11, 0.3)";
                          }
                        }}
                      >
                        {itemValidations[item.lineItemKey] &&
                        (!itemValidations[item.lineItemKey].productTypeValid ||
                          !itemValidations[item.lineItemKey].colorValid)
                          ? "⚠️ Fix Validation First"
                          : "📤 Upload Designs"}
                      </button>
                      {/* 🔄 Design Status alanı - item bazında kontrol */}
                      {item.design_uploaded && (
                        <div
                          style={{
                            backgroundColor: "#dcfce7", // Açık yeşil zemin
                            color: "#16a34a", // Yeşil yazı
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            fontSize: "13px",
                            marginBottom: "8px",
                            display: "inline-block",
                          }}
                        >
                          ✅ Uploaded
                        </div>
                      )}
                    </td>

                    <td style={cellStyle}>{item.sku}</td>
                    <td style={cellStyle}>${item.unitPrice}</td>
                    <td style={cellStyle}>{item.quantity}</td>
                    <td style={cellStyle}>
                      <button
                        onClick={() => handleCopy(item)}
                        disabled={!isCopyEnabled}
                        style={{
                          backgroundColor: isCopyEnabled
                            ? "#007bff"
                            : "#6c757d",
                          color: "white",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: isCopyEnabled ? "pointer" : "not-allowed",
                        }}
                      >
                        Copy
                      </button>

                      <button
                        onClick={handleCopyBarcode}
                        disabled={!isCopyEnabled}
                        style={{
                          backgroundColor: isCopyEnabled
                            ? "#28a745"
                            : "#6c757d",
                          color: "white",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: isCopyEnabled ? "pointer" : "not-allowed",
                          display: "block",
                          width: "100%",
                          marginTop: "5px",
                        }}
                      >
                        Copy Barcode
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h4>Notes</h4>
          <label>Internal Note:</label>
          <textarea
            value={
              editNotes.internalNote === "No notes available"
                ? ""
                : editNotes.internalNote
            }
            onChange={(e) => handleNoteChange("internalNote", e.target.value)}
            style={{ width: "100%", minHeight: "50px" }}
          />
          <label>Customer Note:</label>
          <textarea
            value={
              editNotes.customerNotes === "No notes available"
                ? ""
                : editNotes.customerNotes
            }
            onChange={(e) => handleNoteChange("customerNotes", e.target.value)}
            style={{ width: "100%", minHeight: "50px", marginTop: "10px" }}
          />

          {order.giftMessage && (
            <div
              style={{
                marginTop: "15px",
                padding: "10px",
                background: "#f9f9f9",
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <strong>Gift Note:</strong>
              <p>{order.giftMessage}</p>
            </div>
          )}

          <button
            className="btn btn-primary mt-2"
            onClick={saveNotes}
            disabled={saving}
          >
            Save Notes
          </button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            className="btn"
            style={{
              backgroundColor: prevOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: prevOrderId ? "pointer" : "not-allowed",
              pointerEvents: prevOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${prevOrderId}`, {
                state: {
                  orders: ordersList,
                  from: isEmbroideryUser
                    ? "/embroidery-my-orders"
                    : "/my-orders",
                },
              })
            }
          >
            ⬅️ Previous
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: nextOrderId ? "#FFA500" : "#ccc",
              color: "white",
              cursor: nextOrderId ? "pointer" : "not-allowed",
              pointerEvents: nextOrderId ? "auto" : "none",
            }}
            onClick={() =>
              navigate(`/order-details/${nextOrderId}`, {
                state: {
                  orders: ordersList,
                  from: isEmbroideryUser
                    ? "/embroidery-my-orders"
                    : "/my-orders",
                },
              })
            }
          >
            Next ➡️
          </button>
        </div>
      </main>
      <UploadDesignModal
        key={`upload-modal-${selectedItem?.lineItemKey || "default"}-${
          selectedItem?.orderItemId || "default"
        }`}
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedItem(null);
          fetchOrderDetails(); // 🔁 tasarım gönderildikten sonra güncelle
        }}
        order={order}
        item={selectedItem}
        itemSku={selectedItem?.sku}
        copyMeta={{
          shopName: order?.storeName || "Shop",
          orderNumber: order?.orderNumber || "Unknown",
          customerName: order?.shipTo?.name || "Customer",
          userName: `${localStorage.getItem("first_name") || ""} ${
            localStorage.getItem("last_name") || "User"
          }`,
        }}
        validatedProductType={
          selectedItem && itemValidations[selectedItem.lineItemKey]?.productType
        }
        validatedColor={
          selectedItem && itemValidations[selectedItem.lineItemKey]?.color
        }
      />
      <style>
        {`
          @keyframes spin {
            0% { transform: translateY(-50%) rotate(0deg); }
            100% { transform: translateY(-50%) rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default OrderDetailsPage;
